'use client'

import { isUndefined } from 'lodash-es'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { useCommonDialog, useSession } from '@/shared/lib'
import { Button } from '@/shared/ui'
import { TextButton } from '@/shared/ui/text-button'

import { useBadges } from '../../lib/badges'
import { Layout } from '../layout'
import * as Navigation from '../navigation'
import { ProfileButton } from './profile-button'

const PATH = {
  GUEST_MAIN: '/guest/main',
  HOST_MAIN: '/host/main',
} as const

type Props = {
  wide?: boolean
  isLoggedIn?: boolean
  isHost?: boolean
  userId?: number
  className?: string
}

export const NavigationBarUI = ({
  wide = false,
  isLoggedIn: isLoggedInProp = false,
  isHost: isHostProp = false,
  userId: userIdProp,
  className,
}: Props) => {
  const pathname = usePathname()
  const session = useSession()

  const isLoggedIn = isUndefined(session.data) ? isLoggedInProp : !!session.data
  const isHost = isUndefined(session.data)
    ? isHostProp
    : (session.data?.isHost ?? false)
  const isHostMode = !!pathname?.startsWith('/host')

  const { openMoveToSignInDialog } = useCommonDialog()

  const { badges } = useBadges({ isLoggedIn, userId: userIdProp })

  const guestNavigationItems = [
    {
      href: '/guest/search',
      key: 'search',
      newBadge: false,
      auth: false,
      excludedPaths: [],
    },
    {
      href: '/guest/contract',
      key: 'contract',
      newBadge: badges.badgeGuestBooking > 0,
      auth: true,
      excludedPaths: [],
    },
    {
      href: '/guest/chat',
      key: 'chat',
      newBadge: badges.badgeChat > 0,
      auth: true,
      excludedPaths: [],
    },
  ]

  const hostNavigationItems = [
    {
      href: '/host/room',
      key: 'room',
      newBadge: false,
      auth: true,
      excludedPaths: [
        '/host/room/*path/contract/*path',
        '/host/room/*path/preview',
      ],
    },
    {
      href: '/host/contract',
      key: 'contract',
      newBadge: badges.badgeHostBooking > 0,
      auth: true,
      excludedPaths: [],
    },
    {
      href: '/host/chat',
      key: 'chat',
      newBadge: badges.badgeChat > 0,
      auth: true,
      excludedPaths: [],
    },
  ]

  const navigationItems = isHostMode
    ? hostNavigationItems
    : guestNavigationItems

  const handleNotAccessible = (pathname: string) => {
    openMoveToSignInDialog(pathname)
  }

  return (
    <Layout wide={wide} className={className}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <Navigation.List>
          {navigationItems.map(
            ({ href, key, newBadge, auth, excludedPaths }) => (
              <Navigation.Item
                href={href}
                key={key}
                newBadge={newBadge}
                accessible={isLoggedIn || !auth}
                onNotAccessible={handleNotAccessible}
                excludedPaths={excludedPaths}
              >
                {key}
              </Navigation.Item>
            ),
          )}
        </Navigation.List>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-4">
        {(() => {
          if (!isHostMode && !isHost) {
            return (
              <TextButton asChild color="gray" size="small" weight="normal">
                <Link href={PATH.HOST_MAIN}>삼삼엠투 호스트 시작하기</Link>
              </TextButton>
            )
          }

          return (
            <TextButton color="gray" size="small" weight="normal" asChild>
              <Link href={isHostMode ? PATH.GUEST_MAIN : PATH.HOST_MAIN}>
                {isHostMode ? '게스트 모드로 전환' : '호스트 모드로 전환'}
              </Link>
            </TextButton>
          )
        })()}
        {!isLoggedIn && (
          <Button
            asChild
            variant="outline"
            color="primary"
            size="small"
            weight="medium"
          >
            <Link href="/sign-in">로그인/회원가입</Link>
          </Button>
        )}
        <ProfileButton isHostMode={isHostMode} isLoggedIn={isLoggedIn} />
      </div>
    </Layout>
  )
}
